import { useState, useEffect } from "react";
import { useForm } from "@formspree/react";
import { toast } from "sonner";
import { FORMSPREE_FORM_ID } from "@/lib/formspree";
import { trackWhatsAppClick } from "@/lib/analytics";
import {
    WHATSAPP_HREF,
    DISCOVERY_CALL_HREF,
    STUDIO_EMAIL,
    EMAIL_HREF,
    SOCIALS,
} from "@/lib/links";
import { DB } from "@/lib/media";

const STAGE_OPTIONS = [
    "Discover — strategy & positioning",
    "Strategy — messaging & roadmap",
    "Create — brand, website & content",
    "Launch — campaigns & activation",
    "Scale — data, AI & growth",
    "Not sure yet",
];

const SOCIAL_LINKS = [
    { label: "Instagram", href: SOCIALS.instagram, key: "instagram" },
    { label: "LinkedIn", href: SOCIALS.linkedin, key: "linkedin" },
    { label: "Facebook", href: SOCIALS.facebook, key: "facebook" },
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        stage: STAGE_OPTIONS[0],
        message: "",
    });
    const [state, handleSubmit] = useForm(FORMSPREE_FORM_ID);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    // On success: reset fields + toast. On error: toast.
    useEffect(() => {
        if (state.succeeded) {
            toast.success(
                "Thanks for reaching out. We've received your enquiry and will get back to you within one business day."
            );
            setForm({
                name: "",
                email: "",
                company: "",
                stage: STAGE_OPTIONS[0],
                message: "",
            });
        }
    }, [state.succeeded]);

    useEffect(() => {
        if (state.errors) {
            toast.error(
                "Something went wrong. Please try again or contact us directly at abhay@digitalbeej.in."
            );
        }
    }, [state.errors]);

    const onSubmit = (e) => {
        if (state.submitting) {
            e.preventDefault();
            return;
        }
        if (!form.name || !form.email || !form.message) {
            e.preventDefault();
            toast.error("Please fill in name, email and a short message.");
            return;
        }
        const ts = e.currentTarget.elements.namedItem("timestamp");
        if (ts) ts.value = new Date().toISOString();
        handleSubmit(e);
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="bg-obsidian text-bone relative pt-24 lg:pt-32 pb-12 grain overflow-hidden"
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
                <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-[0.22em] font-bold text-bone/50">
                        (Contact / 07)
                    </div>
                    <img
                        src={DB.logoWhite}
                        alt="Digitalbeej"
                        className="h-7 w-auto object-contain opacity-70"
                        loading="lazy"
                        decoding="async"
                    />
                </div>

                <h2
                    data-testid="contact-heading"
                    className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.85] text-[18vw] lg:text-[12vw]"
                >
                    Let&apos;s
                    <br />
                    <span className="dark-outline-text">plant it.</span>
                </h2>

                {/* Twin CTAs */}
                <div className="mt-12 lg:mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 max-w-3xl">
                    <a
                        data-testid="contact-cta-whatsapp"
                        href={WHATSAPP_HREF}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                            trackWhatsAppClick({
                                section: "contact_home",
                                buttonName: "contact_home_whatsapp",
                            })
                        }
                        className="group relative bg-olive text-obsidian hover:bg-bone transition-colors duration-300 p-6 lg:p-8 border border-bone/10"
                    >
                        <div className="flex items-center justify-between mb-5">
                            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-obsidian/60">
                                Primary
                            </span>
                            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-obsidian/60">
                                <span className="w-1.5 h-1.5 rounded-full bg-obsidian" />
                                Avg. reply &lt; 2 hrs
                            </span>
                        </div>
                        <div className="font-display font-bold text-2xl lg:text-3xl tracking-tight">
                            Chat on WhatsApp →
                        </div>
                        <p className="mt-3 text-sm text-obsidian/70 max-w-xs">
                            For founders who want a fast, honest conversation —
                            no forms, no funnels.
                        </p>
                    </a>

                    <a
                        data-testid="contact-cta-discovery"
                        href={DISCOVERY_CALL_HREF}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative bg-transparent hover:bg-bone hover:text-obsidian transition-colors duration-300 p-6 lg:p-8 border border-bone/25"
                    >
                        <div className="flex items-center justify-between mb-5">
                            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/55 group-hover:text-obsidian/60">
                                Secondary
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-bone/55 group-hover:text-obsidian/60">
                                20 minutes · Free
                            </span>
                        </div>
                        <div className="font-display font-bold text-2xl lg:text-3xl tracking-tight">
                            Book a discovery call →
                        </div>
                        <p className="mt-3 text-sm text-bone/65 group-hover:text-obsidian/70 max-w-xs">
                            Pick a slot. We&apos;ll come prepared with a
                            point-of-view on your brand.
                        </p>
                    </a>
                </div>

                <div className="mt-20 grid grid-cols-12 gap-y-12 lg:gap-x-12">
                    {/* Left: studio details */}
                    <div className="col-span-12 lg:col-span-5">
                        <p className="font-serif italic text-2xl lg:text-3xl text-bone/80 max-w-md leading-snug">
                            Prefer email? Send us a few lines about your brand
                            and where you are — we&apos;ll reply within one
                            business day.
                        </p>

                        <div className="mt-12 space-y-8">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45">
                                    Write to us
                                </div>
                                <a
                                    data-testid="contact-email"
                                    href={EMAIL_HREF}
                                    className="mt-2 inline-block font-display font-bold text-xl lg:text-2xl link-underline"
                                >
                                    {STUDIO_EMAIL}
                                </a>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45">
                                    WhatsApp
                                </div>
                                <a
                                    data-testid="contact-whatsapp-number"
                                    href={WHATSAPP_HREF}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 inline-block font-display font-bold text-xl lg:text-2xl link-underline"
                                >
                                    +91 89288 64286
                                </a>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45">
                                    Studio
                                </div>
                                <div className="mt-2 text-lg text-bone/85">
                                    Mumbai · India
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45">
                                    Follow
                                </div>
                                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                                    {SOCIAL_LINKS.map((s) => (
                                        <a
                                            key={s.key}
                                            href={s.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`contact-social-${s.key}`}
                                            className="text-sm font-medium link-underline"
                                        >
                                            {s.label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: form */}
                    <form
                        data-testid="contact-form"
                        onSubmit={onSubmit}
                        className="col-span-12 lg:col-span-7 lg:pl-10"
                    >
                        {/* Hidden tracking fields (not shown in UI) */}
                        <input
                            type="hidden"
                            name="_subject"
                            value="New Digitalbeej Website Enquiry"
                        />
                        <input type="hidden" name="source" value="digitalbeej.in" />
                        <input
                            type="hidden"
                            name="page"
                            value={
                                typeof window !== "undefined"
                                    ? window.location.href
                                    : ""
                            }
                        />
                        <input
                            type="hidden"
                            name="timestamp"
                        />
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-10">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45">
                                    Your name *
                                </label>
                                <input
                                    data-testid="contact-input-name"
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={update("name")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-bone/30 focus:border-bone outline-none py-3 text-lg placeholder-bone/30 transition-colors"
                                    placeholder="Ada Lovelace"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45">
                                    Email *
                                </label>
                                <input
                                    data-testid="contact-input-email"
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-bone/30 focus:border-bone outline-none py-3 text-lg placeholder-bone/30 transition-colors"
                                    placeholder="you@brand.com"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45">
                                    Company
                                </label>
                                <input
                                    data-testid="contact-input-company"
                                    type="text"
                                    name="company"
                                    value={form.company}
                                    onChange={update("company")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-bone/30 focus:border-bone outline-none py-3 text-lg placeholder-bone/30 transition-colors"
                                    placeholder="Your brand"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45">
                                    Where are you in the process?
                                </label>
                                <select
                                    data-testid="contact-input-stage"
                                    name="stage"
                                    value={form.stage}
                                    onChange={update("stage")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-bone/30 focus:border-bone outline-none py-3 text-lg text-bone transition-colors appearance-none"
                                >
                                    {STAGE_OPTIONS.map((opt) => (
                                        <option
                                            key={opt}
                                            value={opt}
                                            className="text-obsidian"
                                        >
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45">
                                    Tell us about your brand *
                                </label>
                                <textarea
                                    data-testid="contact-input-message"
                                    rows={4}
                                    name="message"
                                    value={form.message}
                                    onChange={update("message")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-bone/30 focus:border-bone outline-none py-3 text-lg placeholder-bone/30 resize-none transition-colors"
                                    placeholder="A few lines about your brand, goals, timeline & budget…"
                                />
                            </div>
                        </div>

                        <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
                            <p className="text-xs text-bone/45 max-w-sm">
                                Your details stay between us. No newsletter
                                subscriptions, no spam — ever.
                            </p>
                            <button
                                data-testid="contact-submit"
                                type="submit"
                                disabled={state.submitting}
                                className="group inline-flex items-center gap-3 bg-bone text-obsidian px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-olive transition-colors duration-300 disabled:opacity-60"
                            >
                                {state.submitting ? "Sending…" : "Send enquiry"}
                                <span
                                    aria-hidden
                                    className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                                >
                                    →
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}
